import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Collapse, IconButton } from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import { vacancyService } from '../../../../../services/vacancy.service';
import type {
  VacancyStage,
  VacancyStageQuestion,
} from '../../../../../services/types/vacancy.types';
import Loading from '../../../../common/Loading';
import QuestionBooleanIcon from '../../../../common/icons/QuestionBooleanIcon';
import QuestionCurrencyIcon from '../../../../common/icons/QuestionCurrencyIcon';
import QuestionDateIcon from '../../../../common/icons/QuestionDateIcon';
import QuestionDecimalIcon from '../../../../common/icons/QuestionDecimalIcon';
import QuestionNumberIcon from '../../../../common/icons/QuestionNumberIcon';
import QuestionTextIcon from '../../../../common/icons/QuestionTextIcon';

const ANSWER_TYPE_ICON: Record<VacancyStageQuestion['answerType'], React.ReactElement> = {
  text: <QuestionTextIcon />,
  boolean: <QuestionBooleanIcon />,
  number: <QuestionNumberIcon />,
  currency: <QuestionCurrencyIcon />,
  date: <QuestionDateIcon />,
  decimal: <QuestionDecimalIcon />,
};

/** A vacancy stage with its questions in display order. */
interface StageWithQuestions {
  stage: VacancyStage;
  questions: VacancyStageQuestion[];
}

interface ViewVacancyInterviewQuestionsProps {
  vacancyId: number;
}

const ViewVacancyInterviewQuestions: FC<ViewVacancyInterviewQuestionsProps> = ({ vacancyId }) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stages, setStages] = useState<StageWithQuestions[] | null>(null);

  useEffect(() => {
    if (!expanded || stages !== null) return;

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const stageResponse = await vacancyService.getVacancyStages(vacancyId);
        const loaded = await Promise.all(
          (stageResponse.data.data || []).map(async (stage) => {
            const questionResponse = await vacancyService.getVacancyStageQuestions(vacancyId, stage.id);
            const questions = [...(questionResponse.data.data || [])].sort(
              (a, b) => a.sortOrder - b.sortOrder,
            );
            return { stage, questions };
          }),
        );
        if (!cancelled) {
          // GENERAL first, then the interview stages in pipeline order.
          loaded.sort((a, b) => {
            if (a.stage.stageType !== b.stage.stageType) {
              return a.stage.stageType === 'GENERAL' ? -1 : 1;
            }
            return a.stage.sortOrder - b.stage.sortOrder;
          });
          setStages(loaded);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setError('Failed to load interview questions.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [expanded, vacancyId, stages]);

  const visibleStages = stages ? stages.filter(({ questions }) => questions.length > 0) : null;
  const hasAny = visibleStages !== null && visibleStages.length > 0;

  return (
    <Box className="view-vacancy-section" sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '10px' }}>
        <div className="job-description-title">Interview Questions</div>
        <IconButton
          type="button"
          data-testid="interview-questions-toggle"
          onClick={() => setExpanded((prev) => !prev)}
          className="jd-expand-collapse-button"
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse interview questions' : 'Expand interview questions'}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        {loading && (
          <Box className="interview-questions-loading" sx={{ py: 2 }}>
            <Loading />
          </Box>
        )}
        {!loading && error && (
          <Box className="interview-questions-error" sx={{ py: 1 }}>
            {error}
          </Box>
        )}
        {!loading && !error && visibleStages && !hasAny && (
          <Box className="interview-questions-empty" sx={{ py: 1 }}>
            No interview questions configured for this vacancy.
          </Box>
        )}
        {!loading && !error && visibleStages && hasAny && (
          <Box className="interview-questions-body" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {visibleStages.map(({ stage, questions }) => (
              <Box key={stage.id} className="interview-questions-stage">
                <div className="interview-questions-stage-title">
                  {stage.name}
                  {stage.jiraStatusName ? ` · ${stage.jiraStatusName}` : ''}
                </div>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.5 }}>
                  {questions.map((q, index) => (
                    <Box key={q.id} className="interview-questions-row">
                      <span className="interview-questions-row-number">{index + 1}</span>
                      <span className="interview-questions-row-icon">
                        {ANSWER_TYPE_ICON[q.answerType] ?? <QuestionTextIcon />}
                      </span>
                      <span className="interview-questions-row-text">{q.question}</span>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

export default ViewVacancyInterviewQuestions;
